import { FontIcon } from "common/assets";
import {
  Box,
  FormControlProps,
  SingleSelect,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  createFilterOptions,
} from "common/components";
import { Icon, SelectionMode, ValidValue } from "core/api";
import { useTranslation } from "i18n";
import { ReactElement, SyntheticEvent, useEffect } from "react";
import { theme } from "styles/theme";
import {
  FormControlledFieldProps,
  withFormController,
} from "../FormController";

export interface FormSelectProps extends FormControlledFieldProps {
  selectionMode?: SelectionMode;
  /**  An array of ValidValues that will be displayed as options in the select. */
  validValues?: ValidValue[];
  /** Optional. Helper text that is displayed in the input box when empty */
  helperText?: string;
  /**  If set, allow to clear the select by pressing the x. */
  includeEmptyOption?: boolean;
  /** If auto focus on the select */
  autoFocus?: boolean;
  /**  Form control props */
  FormControlProps?: FormControlProps;
}
/**
 * Adds form handling for FormSelect.
 *
 * Depending on selectionMode it is rendered as a dropdown or buttons with icon and text or just icons.
 *
 * @param fieldName Static string used for the unique ids of the inner components.
 * @param control Form control.
 * @param validValues The list of validValues.
 * @param selectionMode - if to render the options as a select or as toggle-buttons.
 * @param props Any remaining properties.
 * @param includeEmptyOption If set, allow to clear the select by pressing the x.
 */
export const FormSelect = withFormController<FormSelectProps>(
  ({
    helperText,
    renderState,
    autoFocus,
    validValues,
    selectionMode = SelectionMode.DROPDOWN,
    ...props
  }): ReactElement => {
    const { t } = useTranslation(["common"]);
    const {
      field,
      fieldState: { error },
      gridColumn,
      isDisabled,
      setExtendedRules,
    } = renderState;

    useEffect(() => {
      setExtendedRules({
        validate: {
          notEmptyRequiredValue: (value) => {
            if (
              props.required &&
              (value === null || (Array.isArray(value) && value.length === 0))
            ) {
              return t("common:forms.fieldIsMandatory");
            }
            return true;
          },
        },
      });
    }, [setExtendedRules, t, props.required]);

    // Remove the 'fieldName' prop from the input props for <SingleSelect>
    // This avoids the warning "React does not recognize the `fieldName` prop on a DOM element."
    const filteredProps: any = { ...props };
    delete filteredProps.fieldName;

    const selectedValue = getSelectedValue(field.value, validValues ?? []);

    /**
     * Handles the change event for a single selection input.
     *
     * If `selectionMode` is `"DROPDOWN"`, `null` values are allowed.
     * Otherwise, `null` is ignored, and the field is not updated.
     *
     * @param _event - The synthetic event triggered by the change.
     * @param value - The selected value, which can be a valid value or null.
     */
    const handleSingleChange = (
      _event: SyntheticEvent,
      value: ValidValue | null
    ) => {
      if (value !== null || selectionMode === "DROPDOWN") {
        field.onChange(value ? value.id : "-1");
      }
    };

    if (selectionMode === "DROPDOWN") {
      return (
        <SingleSelect<ValidValue>
          fullWidth
          autoFocus={autoFocus}
          id={selectedValue?.id}
          dataCyValue={props["data-cy"]}
          disabled={isDisabled}
          required={props.required}
          helperText={error?.message ?? helperText}
          value={selectedValue}
          groupBy={(option) => getGroupBy(option, validValues ?? [])}
          label={props.label || ""}
          includeEmptyOption={props.includeEmptyOption ?? false}
          onChange={handleSingleChange}
          options={validValues ?? []}
          getOptionId={getId}
          getOptionName={getName}
          getIcon={getIcon}
          filterOptions={filterOptions}
          {...filteredProps}
          formControlProps={{
            ...props.FormControlProps,
            sx: [
              { gridColumn },
              ...((Array.isArray(props.FormControlProps?.sx)
                ? props.FormControlProps?.sx
                : [props.FormControlProps?.sx]) ?? []),
            ],
          }}
        />
      );
    } else {
      return (
        <Box
          display="flex"
          justifyContent="flex-end"
          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={selectedValue}
            onChange={handleSingleChange}
            color="primary"
          >
            {validValues?.map((v) => {
              return renderToggleButton(v, selectionMode);
            })}
          </ToggleButtonGroup>
        </Box>
      );
    }
  }
);

/**
 * Renders a toggle button with icon and tooltip.
 *
 * - If `selectionMode` is `BUTTONS`, the button also displays the name.
 *
 * @param v - ValidValue.
 * @param selectionMode - selectionMode.
 * @returns A JSX element representing the toggle button with a tooltip.
 */
function renderToggleButton(
  v: ValidValue,
  selectionMode: SelectionMode
): JSX.Element {
  return (
    <Tooltip
      title={
        <Box sx={{ whiteSpace: "pre-line" }}>
          {`${v.name}\n`}
          {v.description}
        </Box>
      }
    >
      <ToggleButton value={v} data-cy={v.name.replace(/ /g, "")}>
        {(selectionMode === SelectionMode.ICONS ||
          selectionMode === SelectionMode.BUTTONS) &&
          v.icon?.name && <FontIcon icon={v.icon.name} />}
        {selectionMode === SelectionMode.BUTTONS && (
          <Box ml={theme.spacing(0.5)}>{v.name}</Box>
        )}
      </ToggleButton>
    </Tooltip>
  );
}

/**
 *  Helper function to provide the options id
 *
 * @param option
 *
 * @returns the option's id
 */
function getId(option: ValidValue): string {
  return option.id;
}

/**
 *  Helper function to provide the options name
 *
 * @param option
 *
 * @returns the options name
 */

function getName(option: ValidValue): string {
  return option.name;
}

/**
 *  Helper function to provide the options optional groupBy string
 *
 * @param option
 *
 * @returns the options groupBy string, or an empty string if not set or there is just one unique group.
 */
function getGroupBy(option: ValidValue, options: ValidValue[]): string {
  const uniqueGroups = new Set(options.map((opt) => opt.groupBy)).size;

  // Return groupBy if there are more than 1 unique group, otherwise return an empty string
  return uniqueGroups > 1 ? option.groupBy || "" : "";
}
/**
 * Return the selected value as a ValidValue
 *
 * @param value - field value ID
 * @param optionItems
 *
 * @returns
 */
function getSelectedValue(
  value: string | number,
  optionItems: ValidValue[]
): ValidValue | undefined {
  const selectedOption = optionItems.find(
    (option) => option.id === value?.toString()
  );
  return selectedOption || undefined;
}

/**
 * Configures filtering options
 *
 * Matches from the start of the options stringified name.
 *
 * @constant
 * @type {ReturnType<typeof createFilterOptions>}
 */
const filterOptions = createFilterOptions({
  matchFrom: "start",
  stringify: (option: ValidValue) => option.name,
});

/**
 *  Helper function to provide the optional icon
 *
 * @param option
 *
 * @returns the options icon
 */
function getIcon(option: ValidValue): Icon | undefined {
  return option.icon;
}
